import React, { useRef, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion, useInView } from 'motion/react';
import { ArrowRight, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';
import Layout from '../components/layout/Layout';
import SEOHead from '../components/SEOHead';
import { allServicePages } from '../data/servicePages';

const TextReveal = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <div ref={ref} className="overflow-hidden">
      <motion.div initial={{ y: "100%", opacity: 0 }} animate={isInView ? { y: 0, opacity: 1 } : {}} transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}>
        {children}
      </motion.div>
    </div>
  );
};

const FAQItem = ({ q, a, open, onClick }) => (
  <div className="border-b border-[#FF6B35]/10 last:border-0">
    <button onClick={onClick} className="w-full py-5 flex items-center justify-between text-left group" aria-expanded={open}>
      <span className="text-[#2D2D2D] font-medium pr-4 group-hover:text-[#FF6B35] transition-colors">{q}</span>
      {open ? <ChevronUp size={18} className="text-[#FF6B35] flex-shrink-0" /> : <ChevronDown size={18} className="text-[#8A8A8A] flex-shrink-0" />}
    </button>
    <motion.div initial={false} animate={{ height: open ? 'auto' : 0, opacity: open ? 1 : 0 }} className="overflow-hidden">
      <p className="pb-5 text-[#5A5A5A] text-sm leading-relaxed">{a}</p>
    </motion.div>
  </div>
);

const ServicePage = () => {
  const { slug } = useParams();
  const data = allServicePages[slug];
  const [openFaq, setOpenFaq] = useState(null);

  if (!data) return <Navigate to="/digital" replace />;

  const schemas = [
    { "@context": "https://schema.org", "@type": "Service", "name": data.title, "provider": { "@type": "Organization", "name": "Buzz Craft Maven" }, "description": data.metaDesc, "areaServed": ["India", "Dubai", "UAE"] },
    { "@context": "https://schema.org", "@type": "BreadcrumbList", "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://buzzcraftmaven.com" },
      { "@type": "ListItem", "position": 2, "name": data.parent.name, "item": `https://buzzcraftmaven.com${data.parent.path}` },
      { "@type": "ListItem", "position": 3, "name": data.title, "item": data.canonical }
    ]},
    { "@context": "https://schema.org", "@type": "FAQPage", "mainEntity": data.faqs.map(f => ({ "@type": "Question", "name": f.q, "acceptedAnswer": { "@type": "Answer", "text": f.a } })) }
  ];

  // Add HowTo schema if process steps exist
  const processSection = data.sections.find(s => s.steps);
  if (processSection) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "HowTo",
      "name": `How ${data.title} Works at BCM`,
      "description": data.metaDesc,
      "step": processSection.steps.map((step, i) => ({
        "@type": "HowToStep",
        "position": i + 1,
        "name": step.title,
        "text": step.desc
      }))
    });
  }

  // Add Speakable for AI assistants
  schemas.push({
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": data.metaTitle,
    "speakable": {
      "@type": "SpeakableSpecification",
      "cssSelector": ["[data-testid='service-h1']", ".service-intro"]
    },
    "url": data.canonical
  });

  return (
    <Layout>
      <SEOHead title={data.metaTitle} description={data.metaDesc} canonical={data.canonical} schemas={schemas} />

      {/* Hero */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 bg-sunrise-hero relative overflow-hidden" aria-label="Service hero">
        <div className="absolute top-10 right-[10%] w-[350px] h-[350px] sun-orb sun-orb-primary opacity-30" />
        <div className="container mx-auto px-6 md:px-12 relative z-10">
          <nav className="text-sm text-[#8A8A8A] mb-8" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-[#FF6B35]">Home</Link>
            <span className="mx-2">/</span>
            <Link to={data.parent.path} className="hover:text-[#FF6B35]">{data.parent.name}</Link>
            <span className="mx-2">/</span>
            <span className="text-[#2D2D2D]">{data.title}</span>
          </nav>
          <div className="max-w-3xl">
            <TextReveal>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#2D2D2D] tracking-tight leading-[1.05] mb-6" data-testid="service-h1">
                {data.h1.split(' ').slice(0, -2).join(' ')}{' '}
                <span className="text-gradient-sunrise">{data.h1.split(' ').slice(-2).join(' ')}</span>
              </h1>
            </TextReveal>
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
              className="service-intro text-base md:text-lg text-[#5A5A5A] max-w-xl leading-relaxed mb-8">
              {data.intro}
            </motion.p>
            <Link to="/contact">
              <motion.button className="btn-sunrise px-8 py-3.5" whileHover={{ scale: 1.02 }}>
                <span className="flex items-center gap-2">{data.cta} <ArrowRight size={18} /></span>
              </motion.button>
            </Link>
          </div>
        </div>
      </section>

      {/* Content Sections */}
      {data.sections.map((section, si) => (
        <section key={si} className={`py-16 md:py-24 ${si % 2 === 0 ? 'bg-[#FFFBF5]' : 'bg-white'}`} aria-label={section.h2}>
          <div className="container mx-auto px-6 md:px-12">
            <TextReveal>
              <h2 className="font-display text-2xl md:text-3xl text-[#2D2D2D] mb-8">
                {section.h2.split(' ').slice(0, -1).join(' ')}{' '}
                <span className="text-gradient-sunrise">{section.h2.split(' ').slice(-1)}</span>
              </h2>
            </TextReveal>

            {section.content && (
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                className="max-w-3xl">
                {section.content.split('\n\n').map((para, pi) => (
                  <p key={pi} className="text-[#5A5A5A] text-base leading-relaxed mb-4">{para}</p>
                ))}
              </motion.div>
            )}

            {section.steps && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mt-8">
                {section.steps.map((step, i) => (
                  <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                    className="relative p-5 bg-white rounded-2xl border border-[#FF6B35]/8">
                    <span className="font-display text-3xl font-bold text-[#FF6B35]/10 absolute top-3 right-4">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <h3 className="font-semibold text-[#2D2D2D] mb-2 mt-1">{step.title}</h3>
                    <p className="text-[#5A5A5A] text-sm leading-relaxed">{step.desc}</p>
                  </motion.div>
                ))}
              </div>
            )}

            {section.benefits && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-6 max-w-3xl">
                {section.benefits.map((b, i) => (
                  <motion.div key={i} initial={{ opacity: 0, x: -10 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                    className="flex items-start gap-3 p-3 bg-white rounded-xl border border-[#FF6B35]/8">
                    <CheckCircle size={18} className="text-[#FF6B35] flex-shrink-0 mt-0.5" />
                    <span className="text-[#5A5A5A] text-sm">{b}</span>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </section>
      ))}

      {/* FAQ */}
      <section className="py-16 md:py-24 bg-[#FFFBF5]" aria-label="FAQ">
        <div className="container mx-auto px-6 md:px-12 max-w-3xl">
          <TextReveal>
            <h2 className="font-display text-2xl md:text-3xl text-[#2D2D2D] mb-8 text-center">
              Frequently Asked <span className="text-gradient-sunrise">Questions</span>
            </h2>
          </TextReveal>
          <div className="bg-white rounded-2xl p-6 md:p-8 border border-[#FF6B35]/8">
            {data.faqs.map((f, i) => (
              <FAQItem key={i} q={f.q} a={f.a} open={openFaq === i} onClick={() => setOpenFaq(openFaq === i ? null : i)} />
            ))}
          </div>
        </div>
      </section>

      {/* Internal Links */}
      {data.relatedServices && (
        <section className="py-12 bg-white" aria-label="Related services">
          <div className="container mx-auto px-6 md:px-12">
            <h3 className="text-sm font-semibold uppercase tracking-widest text-[#FF6B35] mb-4">Related Services</h3>
            <div className="flex flex-wrap gap-3">
              {data.relatedServices.map(path => {
                const slug = path.split('/').pop();
                const related = allServicePages[slug];
                if (!related) return null;
                return (
                  <Link key={path} to={path} className="px-5 py-2.5 bg-[#FFFBF5] border border-[#FF6B35]/10 rounded-full text-sm text-[#5A5A5A] hover:border-[#FF6B35]/30 hover:text-[#FF6B35] transition-all">
                    {related.title}
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-16 bg-gradient-to-br from-[#FF6B35] to-[#FFB627] text-center">
        <div className="container mx-auto px-6 md:px-12">
          <h2 className="font-display text-3xl md:text-4xl text-white mb-4">Ready to get started?</h2>
          <p className="text-white/80 max-w-md mx-auto mb-8">Let&apos;s discuss your project and build something meaningful together.</p>
          <Link to="/contact">
            <motion.button className="px-10 py-4 bg-white text-[#FF6B35] rounded-full font-semibold shadow-xl hover:shadow-2xl transition-shadow inline-flex items-center gap-2" whileHover={{ scale: 1.02 }}>
              {data.cta} <ArrowRight size={18} />
            </motion.button>
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default ServicePage;
